import { TFPRouteSkeleton } from '../components/tfp-route-skeleton';

/**
 * Shows an animated skeleton in the router outlet while a route is pending
 * (2026-09-16 perceived-latency work).
 *
 * The router cannot paint the new page until its lazily imported chunk has
 * been downloaded, which on a slow link means many seconds of an apparently
 * frozen UI after a tap. `tfp-app` calls `navigationStarted()` from the
 * `vaadin-router-go` window event (fired synchronously in the same task as
 * the tap for `Router.go()`, `<a>` clicks and back/forward alike) and
 * `navigationSettled()` when the new location is committed
 * (`vaadin-router-location-changed`) or the navigation errors.
 *
 * The skeleton appears after `SKELETON_DELAY_MS` rather than immediately so
 * warm navigations (chunk already in the module cache → resolves in a few
 * milliseconds) never flash it, while genuinely slow ones get feedback well
 * inside the ~100-250 ms "instant" perception threshold.
 */
export const SKELETON_DELAY_MS = 150;

export class RoutePendingIndicator {
  private timer: ReturnType<typeof setTimeout> | undefined;

  /** `getOutlet` matches `Router.getOutlet()` so tfp-app can pass it straight through. */
  constructor(private readonly getOutlet: () => Element | DocumentFragment | null | undefined) {}

  /** A navigation began; arm the (delayed) skeleton. Idempotent. */
  navigationStarted(): void {
    if (this.timer !== undefined) {
      return;
    }
    this.timer = setTimeout(() => {
      this.timer = undefined;
      const outlet = this.getOutlet();
      if (!outlet || outlet.querySelector('tfp-route-skeleton')) {
        return;
      }
      outlet.appendChild(new TFPRouteSkeleton());
    }, SKELETON_DELAY_MS);
  }

  /** The navigation committed (or failed): remove any skeleton / unarm timer. Idempotent. */
  navigationSettled(): void {
    if (this.timer !== undefined) {
      clearTimeout(this.timer);
      this.timer = undefined;
    }
    this.getOutlet()?.querySelector('tfp-route-skeleton')?.remove();
  }

  /** Drop a pending timer without touching the outlet (teardown). */
  dispose(): void {
    if (this.timer !== undefined) {
      clearTimeout(this.timer);
      this.timer = undefined;
    }
  }
}
